import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Card, Loading, Btn, EmptyState } from "@/components/primitives";
import { monthOptions, monthLabel } from "@/lib/format";
import { toast } from "sonner";
import { Download, CheckCircle2, Clock, Building2, User, Loader2, BarChart3 } from "lucide-react";

const ACTIVE = ["dispatched", "accepted", "on_scene", "transporting", "delivered"];

function topEntry(obj) {
  const e = Object.entries(obj || {}).sort((a, b) => b[1] - a[1]);
  return e.length ? { name: e[0][0], value: e[0][1] } : null;
}

export default function Reports() {
  const [month, setMonth] = useState("all");
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    setData(null);
    const q = month === "all" ? "" : `?month=${month}`;
    api.get(`/reports/monthly${q}`).then((r) => setData(r.data)).catch(() => setData({ total: 0, by_agency: {}, by_status: {}, by_driver: {}, by_month: {} }));
  }, [month]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const q = month === "all" ? "" : `?month=${month}`;
      const res = await api.get(`/reports/csv${q}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `curtron-report-${month === "all" ? "all" : month}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); } finally { setExporting(false); }
  };

  const tabs = ["all", ...monthOptions(6)];

  return (
    <div className="max-w-3xl">
      <PageHeader title="Reports" subtitle="Monthly summaries & CSV export.">
        <Btn variant="secondary" onClick={exportCsv} disabled={exporting} data-testid="reports-export-btn">
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} CSV
        </Btn>
      </PageHeader>

      <div className="flex items-center gap-1.5 overflow-x-auto pb-1 mb-4 -mx-1 px-1" data-testid="reports-tabs">
        {tabs.map((t) => (
          <button key={t} onClick={() => setMonth(t)} data-testid={`report-tab-${t}`}
            className={`shrink-0 h-8 px-3 rounded-full text-sm font-medium border transition-colors ${month === t ? "bg-curtron-green text-white border-curtron-green" : "bg-curtron-surface text-curtron-sub border-curtron-line hover:bg-curtron-bg"}`}>
            {t === "all" ? "All time" : monthLabel(t)}
          </button>
        ))}
      </div>

      {data === null ? <Loading /> : (() => {
        const total = data.total || 0;
        const completed = data.by_status?.completed || 0;
        const active = ACTIVE.reduce((s, k) => s + (data.by_status?.[k] || 0), 0);
        const pct = total ? Math.round((completed / total) * 100) : 0;
        const topAgency = topEntry(data.by_agency);
        const topDriver = topEntry(data.by_driver);
        const agencyEntries = Object.entries(data.by_agency || {}).sort((a, b) => b[1] - a[1]);

        if (total === 0) return <Card><EmptyState icon={BarChart3} title="No data for this period" /></Card>;

        return (
          <div className="space-y-4">
            <Card className="p-5 bg-curtron-green text-white" data-testid="report-summary">
              <div className="text-xs uppercase tracking-wide text-white/80">Total transports</div>
              <div className="font-heading text-4xl font-bold mt-1">{total}</div>
              <div className="mt-3">
                <div className="h-2 rounded-full bg-white/20 overflow-hidden"><div className="h-full bg-white rounded-full" style={{ width: `${pct}%` }} /></div>
                <div className="text-xs text-white/85 mt-1.5">{pct}% completed</div>
              </div>
            </Card>

            <div className="grid grid-cols-2 gap-4">
              <Card className="p-4" data-testid="report-completed">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-curtron-muted"><CheckCircle2 className="w-4 h-4 text-curtron-green" /> Completed</div>
                <div className="font-heading text-3xl font-bold text-curtron-ink mt-1.5">{completed}</div>
              </Card>
              <Card className="p-4" data-testid="report-active">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-curtron-muted"><Clock className="w-4 h-4 text-curtron-ochre" /> Active</div>
                <div className="font-heading text-3xl font-bold text-curtron-ink mt-1.5">{active}</div>
              </Card>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Card className="p-4">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-curtron-muted"><Building2 className="w-4 h-4" /> Top agency</div>
                <div className="text-sm font-medium text-curtron-ink mt-1.5 truncate">{topAgency?.name || "—"}</div>
                <div className="text-xs text-curtron-muted">{topAgency ? `${topAgency.value} transports` : ""}</div>
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-curtron-muted"><User className="w-4 h-4" /> Top driver</div>
                <div className="text-sm font-medium text-curtron-ink mt-1.5 truncate">{topDriver?.name || "—"}</div>
                <div className="text-xs text-curtron-muted">{topDriver ? `${topDriver.value} transports` : ""}</div>
              </Card>
            </div>

            <Card>
              <div className="px-4 sm:px-5 py-3 border-b border-curtron-line"><h2 className="font-heading font-semibold text-curtron-ink">By Agency</h2></div>
              <div className="divide-y divide-curtron-line">
                {agencyEntries.map(([name, count]) => (
                  <div key={name} className="flex items-center justify-between px-4 sm:px-5 py-3" data-testid={`report-agency-${name}`}>
                    <span className="text-sm text-curtron-ink truncate pr-3">{name}</span>
                    <span className="text-sm font-semibold text-curtron-green shrink-0">{count}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        );
      })()}
    </div>
  );
}
